import { Trans } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { CompanyInsightData } from '../../../../../../hooks/get-company-by-id.hook';
import STRING_KEYS from '../../../../../../language/keys';
import { getAppMode } from '../../../../../../utils/environment';
import { MATCH_PAGE_HEADER_HEIGHT } from '../../../../../../pages/match-page/match.page.styled';
import CompanyInsightsModal from '../../../../../Common/CompanyInsightsModal';
import { AppModeEnum } from '../../../../../../enums/app-mode.enum';
import {
  CompanyLabel,
  ModalBackdrop,
  MultiRoleCompanyLabel,
  SingleRoleCompanyLabel,
} from './experience-item-company-name.styled';

type CompanyNameProps = {
  isSingleRole?: boolean,
  companyData: CompanyInsightData,
  employmentPeriod: string,
  showCompanyInsights: boolean | undefined,
  trackCompanyInsightsClick?: (
    matchExperiencePeriod: string,
    matchExperienceCompanyId: string,
    matchExperienceCompanyName: string,
  ) => void,
}

const isAppInPreviewMode = getAppMode() === AppModeEnum.MatchPreview;

export const CompanyName = ({
  isSingleRole,
  companyData,
  showCompanyInsights,
  employmentPeriod,
  trackCompanyInsightsClick,
}: CompanyNameProps):JSX.Element => {
  const [isCompanyModalVisible, setCompanyModalVisible] = useState<boolean>(false);
  const [shouldModalOpenBelow, setShouldModalOpenBelow] = useState<boolean | null>(null);
  const companyInfoModalRef = useRef<HTMLDivElement>(null);
  const companyInfoModalOpeningButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (companyInfoModalRef.current && companyInfoModalOpeningButtonRef.current) {
      const topPosition = companyInfoModalOpeningButtonRef.current.getBoundingClientRect().top;
      const modalHeight = companyInfoModalRef.current.getBoundingClientRect().height;

      if (topPosition - MATCH_PAGE_HEADER_HEIGHT < modalHeight) {
        // no enough space above the name, open the modal below
        setShouldModalOpenBelow(true);
      } else {
        // there is enough space above the name, open the modal above
        setShouldModalOpenBelow(false);
      }
    }
  }, [isCompanyModalVisible]);

  const isCompanyInsightsButtonVisible =
  !!(companyData.companyId && showCompanyInsights && !isAppInPreviewMode);

  const handleOpenCompanyInsights = () => {
    if (isCompanyInsightsButtonVisible) {
      setCompanyModalVisible(true);
    }

    if (typeof trackCompanyInsightsClick === 'function') {
      trackCompanyInsightsClick(
        employmentPeriod,
        companyData.companyId || '',
        companyData.companyName,
      );
    }
  };

  const handleCloseCompanyInsights = () => {
    setCompanyModalVisible(false);
  };

  if (isSingleRole) {
    return (
      <CompanyLabel>
        <SingleRoleCompanyLabel
          ref={ companyInfoModalOpeningButtonRef }
          isInsightsModalVisible={ (isCompanyModalVisible && !isAppInPreviewMode) }
          onClick={ handleOpenCompanyInsights }
          isCompanyInsightsButtonVisible={ isCompanyInsightsButtonVisible }
        >
          <Trans
            defaults={ STRING_KEYS.AT_COMPANY_BOLD }
            values={ { companyName: companyData.companyName } }
            components={ { bold: <strong /> } }
          />

        </SingleRoleCompanyLabel>

        {(isCompanyModalVisible && !isAppInPreviewMode) && (
          <>
            <ModalBackdrop onClick={ handleCloseCompanyInsights } />
            <CompanyInsightsModal
              companyData={ companyData }
              modalRef={ companyInfoModalRef }
              modalPosition={ shouldModalOpenBelow ? 'top' : 'bottom' }
            />
          </>
        )}
      </CompanyLabel>

    );
  }

  return (
    <CompanyLabel>
      <MultiRoleCompanyLabel
        ref={ companyInfoModalOpeningButtonRef }
        isInsightsModalVisible={ (isCompanyModalVisible && !isAppInPreviewMode) }
        onClick={ handleOpenCompanyInsights }
        isCompanyInsightsButtonVisible={ isCompanyInsightsButtonVisible }
      >
        { companyData.companyName }
      </MultiRoleCompanyLabel>

      {(isCompanyModalVisible && !isAppInPreviewMode) && (
        <>
          <ModalBackdrop onClick={ handleCloseCompanyInsights } />
          <CompanyInsightsModal
            companyData={ companyData }
            modalRef={ companyInfoModalRef }
            modalPosition={ shouldModalOpenBelow ? 'top' : 'bottom' }
          />
        </>
      )}
    </CompanyLabel>
  );
};

CompanyName.defaultProps = {
  isSingleRole: false,
  trackCompanyInsightsClick: undefined,
};
